/* eslint-disable no-prototype-builtins */
import { NavbarBreadcrumbs } from 'components/breadcrumb-bar'
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Impersonation } from './impersonation/impersonation'
import { Notifications } from './notifications'
import { HeaderPopover } from './notifications/types'
import { AdminSearch } from './search-website/admin-search'

import { useAccountStore, useAssetStore } from 'store'
import { useShallow } from 'zustand/react/shallow'
import { useDarkModeFlag, useStores } from 'hooks'
import { useWorkspaceAccess, WorkspaceAccess } from 'hooks/use-workspace-access'
import { removeFromLocalStorage } from '../../utils'
import { Tooltip, useInert } from '@clickguard/design-system'
import Settings01 from '@untitled-ui/icons-react/build/esm/Settings01'
import Monitor04 from '@untitled-ui/icons-react/build/esm/Monitor04'
import {
	AccountOverviewCoachmark,
	useAccountOverviewVisibility
} from 'pages/account-overview'
import { UserActionsOverflow } from './user-actions-overflow'
import { PlusButtonOverflow } from './plus-button-overflow'
import { ContentUpdates } from 'pages/admin/content/content-updates'
import { ThemeMenu } from './theme-menu'

const HIDES_ACCOUNT_OVERVIEW: WorkspaceAccess[] = [
	'not-started',
	'no-setup',
	'no-ad-account',
	'cancelled'
]

export const Navbar = () => {
	const navigate = useNavigate()
	const {
		isVisible: isAccountOverviewVisible,
		isLoading: accountOverviewLoading
	} = useAccountOverviewVisibility()
	// Cases 2 (no trial yet) and 3 (no ad account) hide Account overview.
	const { access } = useWorkspaceAccess()
	const breadcrumbsRef = useRef<HTMLDivElement>(null)
	useInert(breadcrumbsRef, access !== 'full')
	const { accountStore, userStore, assetStore } = useStores()
	const { getAllAccounts } = accountStore
	const { getAllAssets } = assetStore
	const { account, accounts } = useAccountStore(
		useShallow((s) => ({
			account: s.account,
			accounts: s.accounts
		}))
	)
	const { accountsWithAssets, demoMode } = useAssetStore(
		useShallow((s) => ({
			accountsWithAssets: s.accountsWithAssets,
			demoMode: s.demoMode
		}))
	)

	const { user } = userStore
	const location = useLocation()
	const darkModeEnabled = useDarkModeFlag()

	const [open, setOpen] = useState(false)
	const [headerPopover, setHeaderPopover] = useState<HeaderPopover>(null)

	const selectedAccount = accountsWithAssets.find((oneAccount) => {
		return oneAccount?.id === account?.id
	})

	useEffect(() => {
		const fetchAccounts = async () => {
			await getAllAccounts()
			await getAllAssets()
			const updatedAccount = accounts.find((a) => a.id === account?.id)
			if (
				updatedAccount &&
				updatedAccount.setup?.step !== account?.setup?.step
			) {
				if (account?.id) {
					accountStore.setAccountForId(account.id)
				}
			}
		}
		fetchAccounts()
	}, [demoMode, account?.setup, user, selectedAccount?.setup?.step])

	const accountHasAssets =
		selectedAccount?.assets && selectedAccount.assets.length > 0

	const [width, setWidth] = useState(window.innerWidth)
	const handleWindowSizeChange = () => {
		setWidth(window.innerWidth)
	}

	useEffect(() => {
		if (
			(selectedAccount?.setup?.step === 'ACCOUNT_CREATED' ||
				selectedAccount?.setup?.step === 'PLAN_SELECTED') &&
			selectedAccount?.billing === null &&
			!user?.email.includes('@tfbnw.net')
		) {
			setOpen(true)
		}
	}, [selectedAccount?.setup?.step, selectedAccount?.setup])

	useEffect(() => {
		window.addEventListener('resize', handleWindowSizeChange)
		return () => {
			window.removeEventListener('resize', handleWindowSizeChange)
		}
	}, [selectedAccount?.setup])

	const isMobile = width < 768
	const [welcomeViewOpen, setWelcomeViewOpen] = useState(true)

	useEffect(() => {
		if (
			selectedAccount?.setup?.step === 'TRIAL_STARTED' ||
			selectedAccount?.billing !== null
		) {
			setWelcomeViewOpen(true)
		}
	}, [selectedAccount, selectedAccount?.billing, selectedAccount?.setup?.step])

	useEffect(() => {
		isMobile && setWelcomeViewOpen(false)
	}, [width])

	const accountId = demoMode && !account?.id ? 'workspace-demo' : account?.id

	const removeImpersonationId = () => {
		removeFromLocalStorage(['selected_account', 'selected_asset'])
		if (localStorage.getItem('imper_userId')) {
			localStorage.removeItem('imper_userId')
		}
	}

	const handleSettingsClick = () => {
		if (accountId && accountId !== 'workspace-demo') {
			navigate(`/settings/workspace/${accountId}/preferences`)
		} else {
			navigate('/settings/user/profile')
		}
	}

	return (
		<div className="app-navbar sticky top-0 z-30 flex-shrink-0 backdrop-blur-[15px] bg-white border border-t-traffic border-b-[#e1e1e1] rounded-t-[12px] px-[16px] py-[8px]">
			<div className="flex items-center justify-between h-[40px]">
				{/* Left side: scope line (+ button, separator, breadcrumbs) */}
				<div className="flex-1 min-w-0 flex items-center gap-[12px]">
					{location.pathname.startsWith('/admin') && (
						<div className="flex-1 mr-4">
							<AdminSearch />
						</div>
					)}
					{!location.pathname.startsWith('/admin') && (
						<div className="flex min-w-0 items-center gap-[12px]">
							{/* Plus button with overflow menu — hidden on Account
							    Overview (not part of that view per Figma). */}
							{!location.pathname.startsWith('/account-overview') && (
								<>
									<PlusButtonOverflow />

									{/* Vertical separator */}
									<div className="flex items-center justify-center h-[17px] w-1 shrink-0">
										<div className="app-navbar-divider w-px h-[17px] bg-t-checkbox" />
									</div>
								</>
							)}
							{accountsWithAssets &&
								accountHasAssets &&
								access !== 'not-started' &&
								access !== 'cancelled' &&
								account?.billing?.subscription !== null && (
									<div ref={breadcrumbsRef}>
										<NavbarBreadcrumbs />
									</div>
								)}
						</div>
					)}
				</div>

				{/* Right side: notifications bell, settings gear, user avatar+chevron */}
				<div className="flex shrink-0 items-center gap-3">
					<Impersonation />

					{isAccountOverviewVisible &&
						!HIDES_ACCOUNT_OVERVIEW.includes(access) && (
							<div className="relative">
								<Tooltip content="Account overview" position="bottom">
									<button
										aria-label="Account overview"
										onClick={() => navigate('/account-overview')}
										className="flex items-center justify-center p-[6px] rounded-[8px] hover:bg-gray-100 transition-colors cursor-pointer"
									>
										<Monitor04
											width={24}
											height={24}
											className="text-[rgb(var(--cg-text-secondary))]"
										/>
									</button>
								</Tooltip>
								<AccountOverviewCoachmark
									onExplore={() => navigate('/account-overview')}
								/>
							</div>
						)}

					{darkModeEnabled && (
						<ThemeMenu
							headerPopover={headerPopover}
							setHeaderPopover={setHeaderPopover}
						/>
					)}

					<ContentUpdates
						accountOverviewVisible={isAccountOverviewVisible}
						accountOverviewLoading={accountOverviewLoading}
					/>

					<Notifications
						headerPopover={headerPopover}
						setHeaderPopover={setHeaderPopover}
					/>

					<Tooltip content="Settings" position="bottom">
						<button
							aria-label="Settings"
							onClick={handleSettingsClick}
							className="flex items-center justify-center p-[6px] rounded-[8px] hover:bg-gray-100 transition-colors cursor-pointer"
						>
							<Settings01
								width={24}
								height={24}
								className="text-[rgb(var(--cg-text-secondary))]"
							/>
						</button>
					</Tooltip>

					<UserActionsOverflow
						removeImpersonationId={removeImpersonationId}
						headerPopover={headerPopover}
						setHeaderPopover={setHeaderPopover}
					/>
				</div>
			</div>
		</div>
	)
}
